import { LoadingState } from "@repo/app-types/client/client";
import {
  getQuestionsForProjectType,
  projectTemplates,
  ProjectType,
  TemplateCategory,
} from "@repo/app-types/constants/projectTypeToDefaultQuestions";
import {
  type InterviewAgentType,
  ProjectEmailDigestFrequency,
  ProjectMode,
  projectSettingsDefaults,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { buildProjectUrlPath } from "@repo/app-types/helpers/adminAppRoutes";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { PageCenteredLayout } from "@repo/ui/pages/centeredLayout";
import { PageHeader } from "@repo/ui/pages/header";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import {
  IconBuilding,
  IconFocusCentered,
  IconShare2,
  IconUserCircle,
} from "@tabler/icons-react";
import { nanoid } from "nanoid";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  selectCompany,
  selectIsRecruitingModeCompany,
} from "../features/company/companySlice.js";
import { createProject } from "../features/projects/projectsSlice.js";
import {
  ProjectsTable,
  type ProjectStatusFilter,
} from "../features/projects/projectsTable.js";
import { CreateJobOpeningButton } from "../features/projects/projectTemplates/createJobOpeningButton.js";
import { CreateProjectModal } from "../features/projects/projectTemplates/createProjectModal.js";
import { CreateRecruitingProjectModal } from "../features/projects/projectTemplates/createRecruitingProjectModal.js";
import { ImportJobPostingsModal } from "../features/projects/projectTemplates/importJobPostingsModal.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { Can } from "../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../hooks/hook.js";

interface NewProjectOptions {
  name: string;
  type: ProjectType;
  mode: ProjectMode;
}

const templateCategories = [
  {
    name: TemplateCategory.Research,
    templates: projectTemplates.filter(
      (p) => p.category === TemplateCategory.Research,
    ),
    icon: IconFocusCentered,
    iconForeground: "text-orange-700",
    iconBackground: "bg-orange-50",
  },
  {
    name: TemplateCategory.SalesAndSuccess,
    templates: projectTemplates.filter(
      (p) => p.category === TemplateCategory.SalesAndSuccess,
    ),
    icon: IconUserCircle,
    iconForeground: "text-green-700",
    iconBackground: "bg-green-50",
  },
  {
    name: TemplateCategory.HrAndRecruiting,
    templates: projectTemplates.filter(
      (p) => p.category === TemplateCategory.HrAndRecruiting,
    ),
    icon: IconBuilding,
    iconForeground: "text-purple-700",
    iconBackground: "bg-purple-50",
  },
];

// Still on the deprecated centered layout: this page stacks two sections that
// each use the old header as a section heading, so it needs a real redesign
// (not a mechanical swap) to adopt `PageHeader`/`PageLayout`.
export function AllProjectsPage(): ReactNode {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const company = useAppSelector(selectCompany);
  const teammate = useAppSelector(selectTeammate);
  if (!company || !teammate) return <></>; // Should not happen

  const isRecruitingModeCompany = useAppSelector(selectIsRecruitingModeCompany);

  const [newProjectOptions, setNewProjectOptions] = useState<
    NewProjectOptions | undefined
  >();
  const [createProjectStatus, setCreateProjectStatus] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const newProjectTemplate = projectTemplates.find(
    (p) => p.type === newProjectOptions?.type,
  );

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilter>("all");

  const statusFilterOptions: SimpleSelectOption<ProjectStatusFilter>[] = [
    {
      label: isRecruitingModeCompany ? "All job openings" : "All projects",
      value: "all",
    },
    {
      label: isRecruitingModeCompany ? "Live job openings" : "Live projects",
      value: "live",
    },
    {
      label: isRecruitingModeCompany
        ? "Paused job openings"
        : "Paused projects",
      value: "paused",
    },
  ];

  const onClickCreate = async ({
    jobDescription,
    interviewAgentType,
    phoneNumberId,
  }: {
    jobDescription?: string;
    interviewAgentType?: InterviewAgentType;
    phoneNumberId?: string;
  }) => {
    if (!newProjectOptions || !newProjectTemplate) return;

    setCreateProjectStatus(LoadingState.LOADING);

    try {
      const newProject = await dispatch(
        createProject({
          name: newProjectOptions.name,
          incentive_quantity:
            newProjectOptions.mode === ProjectMode.VOICE_AGENT ? 0 : 25,
          questions: getQuestionsForProjectType(
            newProjectOptions.type,
            company.name,
            newProjectOptions.mode === ProjectMode.VOICE_AGENT,
          ),
          short_id: nanoid(),
          mode: newProjectOptions.mode,
          settings: {
            ...projectSettingsDefaults,
            ...(interviewAgentType && {
              interview_agent_type: interviewAgentType,
            }),
            send_email_digest_to_all_teammates: false,
            send_email_digest_to_project_owners: true,
            email_digest_frequency: isRecruitingModeCompany
              ? ProjectEmailDigestFrequency.INSTANT
              : ProjectEmailDigestFrequency.DAILY,
          },
          is_live: true,
          job_description: jobDescription,
          phone_number_id: phoneNumberId,
          owner_teammate_ids: [teammate.id],
        }),
      ).unwrap();

      const newProjectId = newProject.id;

      showToast({
        title: `Project successfully created`,
        variant: ToastVariant.Success,
      });

      setCreateProjectStatus(LoadingState.LOADED);
      navigate(buildProjectUrlPath(newProjectId));
    } catch (err) {
      console.error("error creating project", err);
      setCreateProjectStatus(LoadingState.ERROR);
    }
  };

  return (
    <PageCenteredLayout>
      {!isRecruitingModeCompany && (
        <section className="mb-4 flex flex-col gap-y-6">
          <PageHeader heading="New project" />
          <p className="mb-4 text-base text-gray-600">
            {"Get started with a template, or "}
            <span
              className="cursor-pointer text-blue-500 hover:text-blue-600"
              onClick={() => {
                setNewProjectOptions({
                  name: "",
                  type: ProjectType.Custom,
                  mode: ProjectMode.SURVEY,
                });
              }}
            >
              create a project from scratch
            </span>
            .
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {templateCategories.map((c) => (
              <div
                key={c.name}
                className="relative items-start rounded-lg border border-border-strong bg-white p-4 shadow-xs"
              >
                <div className="mb-4 flex flex-row items-center space-x-3">
                  <div>
                    <span
                      className={twMerge(
                        "inline-flex rounded-lg p-2 ring-4 ring-white",
                        c.iconBackground,
                        c.iconForeground,
                      )}
                    >
                      <c.icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {c.name}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col space-y-2">
                  {c.templates.map((t) => (
                    <div
                      key={t.name}
                      className="group flex cursor-pointer flex-row items-center space-x-1"
                    >
                      <span
                        className="text-sm text-gray-600 group-hover:text-blue-500"
                        onClick={() => {
                          setNewProjectOptions({
                            name: t.projectDefaultName,
                            type: t.type,
                            mode: ProjectMode.SURVEY,
                          });
                        }}
                      >
                        {t.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-y-6">
        <PageHeader
          heading={isRecruitingModeCompany ? "Job openings" : "Projects"}
          rightActions={
            <>
              <SimpleSelect
                label={
                  isRecruitingModeCompany
                    ? "Job openings filter"
                    : "Projects filter"
                }
                isLabelHidden
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusFilterOptions}
              />

              <Can
                I={PermissionActions.CREATE}
                a={PermissionSubjects.JOB_OPENINGS}
                passThrough={true}
              >
                {(allowed) => (
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={() => {
                      setIsImportModalOpen(true);
                    }}
                    icon={<IconShare2 className="h-4 w-4" />}
                    isDisabled={!allowed}
                  >
                    Import job postings
                  </Button>
                )}
              </Can>

              <CreateJobOpeningButton
                onClickCreate={() => {
                  setNewProjectOptions({
                    name: "",
                    type: ProjectType.Custom,
                    mode: ProjectMode.VOICE_AGENT,
                  });
                }}
              />
            </>
          }
        />

        <ProjectsTable statusFilter={statusFilter} />
      </section>

      {isRecruitingModeCompany ||
      newProjectOptions?.type === ProjectType.CandidateScreen ? (
        <CreateRecruitingProjectModal
          isOpen={Boolean(newProjectOptions)}
          onClose={() => {
            setNewProjectOptions(undefined);
          }}
        />
      ) : (
        <CreateProjectModal
          isOpen={Boolean(newProjectOptions)}
          onClose={() => {
            setNewProjectOptions(undefined);
          }}
          name={newProjectOptions?.name || ""}
          onNameChange={(name) => {
            if (!newProjectOptions) return;
            setNewProjectOptions({ ...newProjectOptions, name });
          }}
          mode={newProjectOptions?.mode || ProjectMode.SURVEY}
          onModeChange={(mode) => {
            if (!newProjectOptions) return;
            setNewProjectOptions({ ...newProjectOptions, mode });
          }}
          onClickCreate={onClickCreate}
          template={newProjectTemplate}
          company={company}
          loadingState={createProjectStatus}
        />
      )}

      <ImportJobPostingsModal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
        }}
      />
    </PageCenteredLayout>
  );
}
